import { useTranslation } from "react-i18next";
import { GradingInput } from "./GradingInput";
import { GRADE_TYPE_CONDITION, GRADE_TYPE_MANAGEMENT } from "../../utils/GradeUtils";
import "./GradingSectionTable.css";

/** `uiGrade` is on the 0–100 UI scale, not the stored 0–10; null means ungraded. */
export interface GradingSectionRow {
    vesselGradeId: number;
    name: string;
    weight: number;
    type: number;
    uiGrade: number | null;
}

export interface GradingSectionTableProps {
    sections: GradingSectionRow[];
    /** Receives a multiple of 10, or null when the grade is cleared. */
    onGradeChange: (vesselGradeId: number, value: number | null) => void;
    /** Grades are frozen once the report is issued, e.g. at Full Report. */
    readOnly?: boolean;
}

export const GradingSectionTable = ({ sections, onGradeChange, readOnly }: GradingSectionTableProps) => {
    const { t } = useTranslation("locale");

    if (sections.length === 0) {
        return (
            <div className="flex align-items-center justify-content-center m-6">
                <span className="font-semibold">{t("grading.noSections")}</span>
            </div>
        );
    }

    const columnHeaders = () => (
        <div className="col-12 grading-headers grading-col-headers">
            <div className="flex align-items-center">
                <span className="small-text grading-cell-name">{t("grading.table.section")}</span>
                <span className="small-text grading-cell-weight">{t("grading.table.weighting")}</span>
                <span className="small-text grading-cell-grade">{t("grading.table.grade")}</span>
            </div>
        </div>
    );

    const entry = (row: GradingSectionRow) => (
        <div key={row.vesselGradeId} className="col-12 grade-entry" data-testid="grade-entry">
            <div className="flex align-items-center grading-row">
                <span className="grade-name grading-cell-name">{row.name}</span>
                {/* Weighting comes from the section template, so it is never editable here. */}
                <span className="grading-cell-weight grading-weight">{row.weight.toFixed(2)}</span>
                <div className="grading-cell-grade">
                    <GradingInput
                        inputId={`grade-${row.vesselGradeId}`}
                        value={row.uiGrade}
                        onChange={(value) => onGradeChange(row.vesselGradeId, value)}
                        readOnly={readOnly}
                    />
                </div>
            </div>
        </div>
    );

    const conditionSections = sections.filter((section) => section.type === GRADE_TYPE_CONDITION);
    const managementSections = sections.filter((section) => section.type === GRADE_TYPE_MANAGEMENT);

    return (
        <div className="grading-sections" data-testid="grading-sections">
            {/* One type per column, so each column reads top to bottom in section order.
                Below lg they stack, so each type keeps a header of its own. */}
            <div className="flex flex-wrap">
                <div className="col-12 lg:col-6 p-0" data-testid="condition-sections">
                    {columnHeaders()}
                    {conditionSections.map(entry)}
                </div>
                {managementSections.length > 0 && (
                    <div className="col-12 lg:col-6 p-0 mt-3 lg:mt-0" data-testid="management-sections">
                        {columnHeaders()}
                        {managementSections.map(entry)}
                    </div>
                )}
            </div>
        </div>
    );
};
